import type { ActivityMessage, ChatAppResponse, VoiceTranscriptTurn } from "@/api/models"

import { api } from "@/lib/api-client"
import { logger } from "@/lib/logger"

export function removeChatSessions(sessionIds: string[]) {
  return api.post("/remove-sessions", { session_ids: sessionIds })
}

export function removeChatSession(sessionId: string) {
  return api.post("/remove-session", { session_id: sessionId })
}

export function addMessageId(response: ChatAppResponse, messageId: string): ChatAppResponse {
  return {
    ...response,
    messageId,
    context: {
      ...response.context,
      message_id: messageId,
    } as ChatAppResponse["context"],
  }
}

export function ensureActivitiesArray(response: ChatAppResponse): ChatAppResponse {
  if (!response.activities) {
    response.activities = []
  } else if (!Array.isArray(response.activities)) {
    logger.warn("Activities exists but is not an array, attempting to fix", response.activities)
    try {
      if (typeof response.activities === "string") {
        response.activities = JSON.parse(response.activities)
      } else {
        response.activities = [response.activities]
      }
    } catch (error) {
      logger.error("Failed to fix activities format", error as Error)
      response.activities = []
    }
  }

  return response
}

export function getLastMessageId(messages: [string, ChatAppResponse][]): string | undefined {
  if (!messages.length) {
    return undefined
  }

  const latestResponse = messages[messages.length - 1][1]
  return latestResponse.messageId ?? latestResponse.context.message_id
}

/**
 * Creates a ChatAppResponse for voice transcripts to add to chat history.
 * Accepts optional activities and tool calls to preserve during the exchange.
 * Voice messages render as normal chat messages with an isVoiceMessage flag.
 */
type VoiceTranscriptResponseOptions = {
  activities?: ActivityMessage[]
  toolCalls?: Array<{ name: string; success: boolean; assistantFiles?: string[] }>
  assistantFiles?: string[]
  sessionId?: string
}

export function createVoiceTranscriptResponse(
  text: string,
  options: VoiceTranscriptResponseOptions = {}
): ChatAppResponse {
  const { activities, toolCalls, assistantFiles: explicitAssistantFiles = [], sessionId } = options
  // Build the activities list, starting with voice indicator
  const allActivities: ActivityMessage[] = [
    {
      type: "activity",
      activity_type: "voice_mode",
      message: "Voice conversation",
      timestamp: Date.now(),
    },
  ]

  // Add any additional activities from the exchange
  if (activities && activities.length > 0) {
    allActivities.push(...activities)
  }

  // Add tool call activities if any
  if (toolCalls && toolCalls.length > 0) {
    for (const tc of toolCalls) {
      const alreadyTracked = allActivities.some(
        (activity) => activity.activity_type === tc.name && activity.details?.success === tc.success
      )
      if (alreadyTracked) continue
      allActivities.push({
        type: "activity",
        activity_type: tc.name,
        message: tc.success ? `${tc.name} completed` : `${tc.name} failed`,
        details: {
          success: tc.success,
          ...(tc.assistantFiles?.length ? { assistant_files: tc.assistantFiles } : {}),
        },
        timestamp: Date.now(),
      })
    }
  }

  const assistantFiles = Array.from(
    new Set([
      ...explicitAssistantFiles,
      ...(toolCalls?.flatMap((toolCall) => toolCall.assistantFiles ?? []) ?? []),
    ])
  )

  return {
    content: text,
    context: {
      data_points: [],
      thoughts: [],
      followup_questions: [],
      ...(sessionId ? { session_id: sessionId } : {}),
    },
    session_state: null,
    completed: true,
    activities: allActivities,
    ...(assistantFiles.length ? { assistant_files: assistantFiles } : {}),
    isVoiceMessage: true,
  }
}

type VoiceContentItem = [string, { content?: string; source?: string }]

/** The sources the voice routes save under: GPT Realtime and GPT-Live. */
const VOICE_HISTORY_SOURCES: ReadonlySet<string> = new Set(["realtime_audio", "live_audio"])

/** V2 rich format from backend */
type VoiceContentV2 = {
  source: "realtime_audio" | "live_audio"
  version: "2.0"
  assistant_files?: string[]
  exchanges: Array<{
    user_text: string
    assistant_text: string
    tool_calls?: Array<{
      name: string
      call_id: string
      arguments?: Record<string, unknown>
      result?: Record<string, unknown>
      success: boolean
      assistant_files?: string[]
      timestamp?: number
    }>
    activities?: Array<{
      activity_type: string
      message: string
      details?: Record<string, unknown>
      timestamp?: number
    }>
    assistant_files?: string[]
    timestamp?: number
  }>
}

/**
 * Check if content is the new V2 rich format.
 */
function isVoiceContentV2(content: unknown): content is VoiceContentV2 {
  if (typeof content !== "object" || content === null) return false
  const obj = content as Record<string, unknown>
  return (
    typeof obj.source === "string" &&
    VOICE_HISTORY_SOURCES.has(obj.source) &&
    obj.version === "2.0" &&
    Array.isArray(obj.exchanges)
  )
}

/**
 * Detect if message content is a voice transcript (multi-turn format from a voice route).
 * Supports both:
 * - Legacy format: [["user", {..., source: "realtime_audio"}], ["assistant", {...}], ...]
 * - V2 format: {source: "realtime_audio" | "live_audio", version: "2.0", exchanges: [...]}
 */
export function isVoiceTranscriptContent(content: unknown): boolean {
  // Check for V2 format first
  if (isVoiceContentV2(content)) {
    return true
  }

  // Check for legacy format
  if (!Array.isArray(content) || content.length === 0) {
    return false
  }

  // Check if it's the multi-turn format: array of ["role", data] tuples
  const firstItem = content[0]
  if (!Array.isArray(firstItem) || firstItem.length !== 2) {
    return false
  }

  const [role, data] = firstItem as VoiceContentItem
  if (role !== "user" || typeof data !== "object") {
    return false
  }

  return typeof data.source === "string" && VOICE_HISTORY_SOURCES.has(data.source)
}

/**
 * Process V2 format voice content into turns with rich data.
 */
function processV2VoiceContent(content: VoiceContentV2): VoiceTranscriptTurn[] {
  const turns = content.exchanges.map((exchange) => ({
    userText: exchange.user_text,
    assistantText: exchange.assistant_text,
    toolCalls: exchange.tool_calls?.map((tc) => ({
      name: tc.name,
      callId: tc.call_id,
      arguments: tc.arguments,
      result: tc.result,
      success: tc.success,
      assistantFiles: tc.assistant_files,
      timestamp: tc.timestamp,
    })),
    activities: exchange.activities?.map((a) => ({
      activityType: a.activity_type,
      message: a.message,
      details: a.details,
      timestamp: a.timestamp,
    })),
    assistantFiles: Array.from(
      new Set([
        ...(exchange.assistant_files ?? []),
        ...(exchange.tool_calls?.flatMap((toolCall) => toolCall.assistant_files ?? []) ?? []),
      ])
    ),
    timestamp: exchange.timestamp,
  }))

  // Session-level refs are a provider-ordering fallback: a tool can finish
  // before the asynchronous input transcript creates its exchange. Attach any
  // otherwise-unassigned files to the final visible turn after reload.
  if (turns.length > 0) {
    const assignedFiles = new Set(turns.flatMap((turn) => turn.assistantFiles))
    const unassignedFiles = (content.assistant_files ?? []).filter(
      (filename) => typeof filename === "string" && filename && !assignedFiles.has(filename)
    )
    if (unassignedFiles.length > 0) {
      const finalTurn = turns[turns.length - 1]
      finalTurn.assistantFiles = Array.from(
        new Set([...finalTurn.assistantFiles, ...unassignedFiles])
      )
    }
  }

  return turns
}

/**
 * Process legacy format voice content into turns.
 */
function processLegacyVoiceContent(content: VoiceContentItem[]): VoiceTranscriptTurn[] {
  const turns: VoiceTranscriptTurn[] = []
  let currentUserText = ""

  for (const item of content) {
    if (!Array.isArray(item)) continue

    const [role, data] = item as VoiceContentItem

    if (role === "user") {
      currentUserText = data.content ?? ""
    } else if (role === "assistant") {
      const assistantText = data.content ?? ""
      if (currentUserText || assistantText) {
        turns.push({
          userText: currentUserText,
          assistantText,
        })
        currentUserText = ""
      }
    }
  }

  // Handle trailing user message without assistant response
  if (currentUserText) {
    turns.push({
      userText: currentUserText,
      assistantText: "",
    })
  }

  return turns
}

/**
 * Process voice transcript message into individual [userQuery, ChatAppResponse] pairs.
 * Each turn in the voice conversation becomes a separate message pair,
 * rendering like normal chat messages instead of a collapsed voice transcript section.
 * Supports both legacy format and V2 rich format with tool calls and activities.
 */
export function processVoiceTranscriptMessage(
  content: VoiceContentItem[] | VoiceContentV2,
  messageId: string
): [string, ChatAppResponse][] {
  // Parse turns based on format
  const turns: VoiceTranscriptTurn[] = isVoiceContentV2(content)
    ? processV2VoiceContent(content)
    : processLegacyVoiceContent(content as VoiceContentItem[])

  // Convert each turn into a separate message pair
  return turns.map((turn, index) => {
    // Build activities for this turn
    const activities: ActivityMessage[] = []

    // Add a subtle voice indicator activity (first turn only)
    if (index === 0) {
      activities.push({
        type: "activity",
        activity_type: "voice_mode",
        message: "Voice conversation",
        timestamp: turn.timestamp ?? Date.now(),
      })
    }

    // Add tool call activities
    if (turn.toolCalls) {
      for (const tc of turn.toolCalls) {
        const alreadyTracked = turn.activities?.some(
          (activity) =>
            activity.activityType === tc.name &&
            typeof activity.details?.success === "boolean" &&
            activity.details.success === tc.success
        )
        if (alreadyTracked) continue
        activities.push({
          type: "activity",
          activity_type: tc.name,
          message: tc.success ? `${tc.name} completed` : `${tc.name} failed`,
          details: {
            success: tc.success,
            result: tc.result,
            ...(tc.assistantFiles?.length ? { assistant_files: tc.assistantFiles } : {}),
          },
          timestamp: tc.timestamp ?? Date.now(),
        })
      }
    }

    // Add other activities from this turn
    if (turn.activities) {
      for (const activity of turn.activities) {
        activities.push({
          type: "activity",
          activity_type: activity.activityType,
          message: activity.message,
          details: activity.details,
          timestamp: activity.timestamp ?? Date.now(),
        })
      }
    }

    const response: ChatAppResponse = {
      content: turn.assistantText,
      context: {
        data_points: [],
        thoughts: [],
        followup_questions: [],
        message_id: `${messageId}-turn-${index}`,
      },
      messageId: `${messageId}-turn-${index}`,
      session_state: null,
      completed: true,
      activities,
      ...(turn.assistantFiles?.length ? { assistant_files: turn.assistantFiles } : {}),
      // Mark as voice message for potential UI hints
      isVoiceMessage: true,
    }

    return [turn.userText, response]
  })
}
